import { typography, radius } from '@/shared/theme/tokens';
import { HUD } from './hudTheme';

const MONO = typography.fontFamily.mono;

/**
 * Glassy translucent panel — the HUD's structural primitive. A thin glowing
 * stroke, soft inner sheen, optional accent corner tick. Read-only chrome only;
 * no interaction lives here.
 */
export function GlassPanel({ accent = HUD.accent, glow, children, style }) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: HUD.panel,
        border: `1px solid ${HUD.stroke}`,
        borderRadius: radius.lg,
        boxShadow: glow
          ? `0 0 0 1px ${HUD.strokeSoft}, 0 18px 40px -28px ${glow}`
          : `0 0 0 1px ${HUD.strokeSoft}`,
        backdropFilter: 'blur(6px)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* top accent hairline */}
      <span
        aria-hidden
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.55,
        }}
      />
      {children}
    </div>
  );
}

/**
 * Panel header — small monospace eyebrow with an accent dot + optional count and
 * right-aligned slot. Mirrors the terminal's PaneHeader role in HUD language.
 */
export function PanelHeader({ title, accent = HUD.accent, count, right }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '9px 12px', borderBottom: `1px solid ${HUD.strokeSoft}`, flexShrink: 0,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: radius.full, background: accent,
        boxShadow: `0 0 8px ${accent}`, flexShrink: 0,
      }}
      />
      <span style={{
        fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
        letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: HUD.inkDim,
      }}>
        {title}
      </span>
      {count != null && (
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
          color: accent, fontVariantNumeric: 'tabular-nums',
          background: HUD.accentTint, border: `1px solid ${HUD.accentEdge}`,
          borderRadius: radius.full, padding: '0 6px', minWidth: 18, textAlign: 'center',
        }}>
          {count}
        </span>
      )}
      {right && <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>{right}</span>}
    </div>
  );
}
